import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { PayrollPolicy } from 'src/app/shared/lib/payroll-effective-dates-history/models/pay-rate.model';
import { PayrollRates } from 'src/app/shared/lib/payroll-effective-dates-history/models/payroll-rates.model';
import { Stipend } from 'src/app/shared/lib/payroll-effective-dates-history/models/stipend.model';
import { combine } from 'src/app/shared/utilities/route';
import { BillingPolicy } from 'src/app/travel-contracts/effective-dates/model/billing-policy.model';
import { BillingRates } from 'src/app/travel-contracts/effective-dates/model/billing-rates.model';
import {
    PayrollChangeSummary,
    PayrollNonMarginRates
} from '../../shared/lib/payroll-effective-dates-summary/models/payroll-change-summary.model';

@Injectable({
    providedIn: 'root'
})
export class EffectiveDatesService {
    private readonly coreApiPath: string;
    private readonly JSONHeaders: HttpHeaders;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
        this.JSONHeaders = new HttpHeaders().set('Content-Type', 'application/json');
    }

    addBillingRate(contractId: number, data: BillingPolicy): Observable<BillingPolicy> {
        const url = `${this.coreApiPath}/billing-rates/contract/${contractId}`;
        const date = data.startDate ? new Date(data.startDate) : new Date();
        return this.http.post<BillingPolicy>(url, { ...data, startDate: date.toLocaleDateString() });
    }

    getEffectiveDatesHistory(contractId: number): Observable<BillingRates> {
        const url = `${this.coreApiPath}/billing-rates/contract/${contractId}`;
        return this.http.get<BillingRates>(url);
    }

    updateBillingRate(contractId: number, data: BillingPolicy): Observable<BillingPolicy> {
        const url = `${this.coreApiPath}/billing-rates/contract/${contractId}`;
        return this.http.put<BillingPolicy>(url, { ...data, startDate: new Date(data.startDate).toLocaleDateString() });
    }

    deleteEffectiveDate(itemId: number): Observable<boolean> {
        const url = `${this.coreApiPath}/billing-rates/contract/${itemId}`;
        return this.http.delete<boolean>(url);
    }

    isBillSection(section): boolean {
        return section === 'Bill Rates';
    }

    isAgencySection(section): boolean {
        return section === 'Agency Pay';
    }

    isOvertimeSection(section): boolean {
        return section === 'Overtime Rules';
    }

    isDoubletimeSection(section): boolean {
        return section === 'Doubletime Rules';
    }

    isRateLocums(rateName: any) {
        const locumsRatesNames = ['Night Call (Daily)', '24hr Call (Daily)', 'Weekend Call (Daily)'];

        return locumsRatesNames.includes(rateName);
    }

    getRates(section: any, data: any, isLocums: boolean) {
        if (!data) {
            return [];
        }

        if (this.isBillSection(section)) {
            let rates = data.filter((item) => item.billType === 'bill');
            if (!isLocums) {
                rates = rates.filter((item) => !this.isRateLocums(item?.payType));
            }
            return rates;
        } else if (this.isAgencySection(section)) {
            let rates = data.filter((item) => item.billType === 'agency');
            if (!isLocums) {
                rates = rates.filter((item) => !this.isRateLocums(item?.payType));
            }
            return rates;
        } else if (this.isOvertimeSection(section)) {
            return data.filter((item) => item.billType === 'overtime');
        } else if (this.isDoubletimeSection(section)) {
            return data.filter((item) => item.billType === 'doubletime');
        } else {
            return [];
        }
    }

    getPayrollRates(contractId: number): Observable<PayrollRates> {
        const url = `${this.coreApiPath}/payroll-rates/contract/${contractId}`;
        return this.http.get<PayrollRates>(url);
    }

    updatePayrollPolicy(data: PayrollPolicy) {
        const url = `${this.coreApiPath}/payroll-rates/contract/${data.contractId}`;
        return this.http.put<PayrollPolicy>(url, {
            ...data,
            startDateUtc: new Date(data.startDateUtc).toLocaleDateString()
        });
    }

    updateExtraPayment(data: Stipend, type: 'Meals' | 'Housing') {
        const url = `${this.coreApiPath}/extra-payments/contract/${data.contractId}/stipend/${type}`;
        return this.http.put<Stipend>(url, {
            ...data,
            startDateUtc: new Date(data.startDateUtc).toLocaleDateString()
        });
    }

    updatePayrollPayNotes(contractId: number, payNotes: string) {
        const url = `${this.coreApiPath}/payroll-rates/contract/${contractId}/pay-notes`;
        return this.http.put<PayrollPolicy>(url, {
            payNotes: payNotes
        });
    }

    deleteExtraEffectiveDate(contractId: number, policyId: number): Observable<boolean> {
        const url = `${this.coreApiPath}/extra-payments/contract/${contractId}/extra/${policyId}`;
        return this.http.delete<boolean>(url);
    }

    deletePayEffectiveDate(contractId: number, policyId: number): Observable<boolean> {
        const url = `${this.coreApiPath}/payroll-rates/contract/${contractId}/policy/${policyId}`;
        return this.http.delete<boolean>(url);
    }

    getPayChangeSummary(contractId: number, marginId: number): Observable<PayrollChangeSummary> {
        const url = `${this.coreApiPath}/payroll-rates/contract/${contractId}/attach-margin/${marginId}/change-summary`;
        return this.http.get<PayrollChangeSummary>(url);
    }

    attachMarginToPayRates(
        contractId: number,
        marginId: number,
        startDate: Date,
        nonMarginRates?: PayrollNonMarginRates
    ): Observable<any> {
        const url = `${
            this.coreApiPath
        }/payroll-rates/contract/${contractId}/attach-margin/${marginId}?StartDate=${startDate.toLocaleDateString()}`;
        return this.http.post(url, nonMarginRates, { headers: this.JSONHeaders });
    }

    revertMarginToPayRates(contractId: number, fromMarginId: number, toMarginId: number) {
        const url = `${this.coreApiPath}/payroll-rates/contract/${contractId}/revert/from-margin/${fromMarginId}/to-margin/${toMarginId}`;
        return this.http.post(url, null);
    }

    attachMarginToExtraPayments(contractId: number, marginId: number, startDate: Date): Observable<any> {
        const url = `${
            this.coreApiPath
        }/extra-payments/contract/${contractId}/attach-margin/${marginId}?StartDate=${startDate.toLocaleDateString()}`;
        return this.http.post(url, null);
    }

    revertMarginToExtraPayments(contractId: number, fromMarginId: number, toMarginId: number) {
        const url = `${this.coreApiPath}/extra-payments/contract/${contractId}/revert/from-margin/${fromMarginId}/to-margin/${toMarginId}`;
        return this.http.post(url, null);
    }
}
